import { useEffect, useRef, useState, useMemo } from "react";
import { Chart } from "chart.js/auto";
import {
  Box,
  Card,
  CardContent,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Stack,
} from "@mui/material";
import {
  BarChart as BarChartIcon,
  TableChart as TableChartIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";

/**
 * Sanitize chart configuration to remove invalid callbacks and problematic options
 * that may have been serialized incorrectly through JSON
 */
function sanitizeChartConfig(config) {
  if (!config || typeof config !== "object") {
    return config;
  }

  // Deep clone to avoid mutating the original
  const sanitized = JSON.parse(JSON.stringify(config));

  // Helper to recursively remove string callbacks and invalid values
  const cleanCallbacks = (obj) => {
    if (!obj || typeof obj !== "object") return;

    for (const key in obj) {
      if (!obj.hasOwnProperty(key)) continue;

      const value = obj[key];

      // Remove callback functions that were serialized as strings
      if (key.toLowerCase().includes("callback") && typeof value === "string") {
        delete obj[key];
        continue;
      }

      // Recursively clean nested objects
      if (typeof value === "object" && value !== null) {
        cleanCallbacks(value);
      }
    }
  };

  cleanCallbacks(sanitized);

  // Ensure we have required top-level properties
  if (!sanitized.type) {
    console.error("[DynamicChart] Missing required 'type' property");
    return null;
  }

  if (!sanitized.data) {
    console.error("[DynamicChart] Missing required 'data' property");
    return null;
  }

  // Ensure options object exists
  if (!sanitized.options) {
    sanitized.options = {};
  }

  // Disable animations to prevent re-rendering issues on hover
  sanitized.options.animation = false;
  
  // Disable hover animations specifically
  sanitized.options.hover = {
    ...sanitized.options.hover,
    animationDuration: 0,
  };

  // Disable animations for transitions
  sanitized.options.transitions = {
    active: {
      animation: {
        duration: 0,
      },
    },
  };

  // Fix tooltip callbacks that might have been serialized
  if (!sanitized.options.plugins) {
    sanitized.options.plugins = {};
  }
  
  if (!sanitized.options.plugins.tooltip) {
    sanitized.options.plugins.tooltip = {};
  }

  // Remove any invalid tooltip callbacks and set safe defaults
  const tooltip = sanitized.options.plugins.tooltip;
  if (tooltip.callbacks) {
    // Check each callback and remove if it's not a function or is a string
    Object.keys(tooltip.callbacks).forEach((key) => {
      const callback = tooltip.callbacks[key];
      if (typeof callback !== "function" && callback !== undefined) {
        delete tooltip.callbacks[key];
      }
    });
    
    // If callbacks object is empty or has issues, remove it entirely
    if (Object.keys(tooltip.callbacks).length === 0) {
      delete tooltip.callbacks;
    }
  }

  // Make the chart responsive
  if (!sanitized.options.responsive) {
    sanitized.options.responsive = true;
  }
  
  if (!sanitized.options.maintainAspectRatio) {
    sanitized.options.maintainAspectRatio = true;
  }

  return sanitized;
}

/**
 * Extract tabular data from Chart.js configuration
 */
function extractTableData(config) {
  if (!config?.data) return null;

  const { labels, datasets } = config.data;
  
  if (!datasets || datasets.length === 0) return null;

  return {
    labels: labels || [],
    datasets: datasets.map((dataset) => ({
      label: dataset.label || "Dataset",
      data: dataset.data || [],
      backgroundColor: dataset.backgroundColor,
      borderColor: dataset.borderColor,
    })),
  };
}

/**
 * Convert chart data to CSV format
 */
function convertToCSV(tableData) {
  if (!tableData) return "";

  const { labels, datasets } = tableData;
  
  // Create header row
  const headers = ["Label", ...datasets.map((ds) => ds.label)];
  const csvRows = [headers.join(",")];

  // Create data rows
  const maxLength = Math.max(labels.length, ...datasets.map((ds) => ds.data.length));
  
  for (let i = 0; i < maxLength; i++) {
    const row = [
      labels[i] || "",
      ...datasets.map((ds) => ds.data[i] !== undefined ? ds.data[i] : ""),
    ];
    csvRows.push(row.join(","));
  }

  return csvRows.join("\n");
}

/**
 * Download CSV file
 */
function downloadCSV(csvContent, filename = "chart-data.csv") {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

/**
 * Render table view of chart data
 */
function ChartDataTable({ tableData }) {
  if (!tableData) return null;

  const { labels, datasets } = tableData;

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <Typography variant="body2" fontWeight="bold">
                Label
              </Typography>
            </TableCell>
            {datasets.map((dataset, idx) => (
              <TableCell key={idx} align="right">
                <Typography variant="body2" fontWeight="bold">
                  {dataset.label}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {labels.map((label, rowIdx) => (
            <TableRow key={rowIdx} hover>
              <TableCell component="th" scope="row">
                {label}
              </TableCell>
              {datasets.map((dataset, colIdx) => (
                <TableCell key={colIdx} align="right">
                  {dataset.data[rowIdx] !== undefined
                    ? dataset.data[rowIdx]
                    : "-"}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * DynamicChart - Renders charts using Chart.js from AI-generated configurations
 * 
 * @param {Object} props
 * @param {Object} props.config - Chart.js configuration object with type, data, and options
 * @returns {JSX.Element}
 */
function DynamicChart({ config }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const [viewMode, setViewMode] = useState("chart");

  // Memoize table data extraction
  const tableData = useMemo(() => extractTableData(config), [config]);

  // Memoize the sanitized config
  const sanitizedConfig = useMemo(() => {
    return sanitizeChartConfig(config);
  }, [config]);

  // Create a stable JSON string for deep comparison to prevent unnecessary re-renders
  const configJSON = useMemo(() => {
    try {
      return JSON.stringify(sanitizedConfig);
    } catch {
      return null;
    }
  }, [sanitizedConfig]);

  useEffect(() => {
    // Destroy chart when switching to table view
    if (viewMode !== "chart" && chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
      return;
    }

    if (!canvasRef.current || !sanitizedConfig || viewMode !== "chart") {
      return;
    }

    // If chart doesn't exist, create it
    if (!chartRef.current) {
      try {
        chartRef.current = new Chart(canvasRef.current, sanitizedConfig);
      } catch (error) {
        console.error("[DynamicChart] Failed to create chart:", error);
        console.error("[DynamicChart] Config was:", sanitizedConfig);
      }
    } else {
      // If chart exists, update its data and options instead of recreating
      try {
        if (sanitizedConfig.data) {
          chartRef.current.data = sanitizedConfig.data;
        }
        if (sanitizedConfig.options) {
          chartRef.current.options = sanitizedConfig.options;
        }
        chartRef.current.update("none"); // 'none' mode = no animations during update
      } catch (error) {
        console.error("[DynamicChart] Failed to update chart:", error);
        // If update fails, try recreating
        if (chartRef.current) {
          chartRef.current.destroy();
          chartRef.current = null;
        }
        try {
          chartRef.current = new Chart(canvasRef.current, sanitizedConfig);
        } catch (createError) {
          console.error("[DynamicChart] Failed to recreate chart:", createError);
        }
      }
    }

    // Cleanup function to destroy chart on unmount
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [configJSON, viewMode, sanitizedConfig]);

  // Don't render if config is invalid
  if (!config) {
    return null;
  }

  const handleExportCSV = () => {
    const csvContent = convertToCSV(tableData);
    if (csvContent) {
      const timestamp = new Date().toISOString().split("T")[0];
      downloadCSV(csvContent, `chart-data-${timestamp}.csv`);
    }
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        backgroundColor: "background.paper",
      }}
    >
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            aria-label="view mode"
          >
            <ToggleButton value="chart" aria-label="chart view">
              <Tooltip title="Chart View">
                <BarChartIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="table" aria-label="table view">
              <Tooltip title="Table View">
                <TableChartIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title="Export as CSV">
            <IconButton
              size="small"
              onClick={handleExportCSV}
              disabled={!tableData}
              aria-label="export csv"
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        {viewMode === "chart" ? (
          <Box sx={{ position: "relative", minHeight: 300 }}>
            <canvas ref={canvasRef} />
          </Box>
        ) : (
          <ChartDataTable tableData={tableData} />
        )}
      </CardContent>
    </Card>
  );
}

export default DynamicChart;

